"use client"

import { addToCart } from "@lib/data/cart"
import { useIntersection } from "@lib/hooks/use-in-view"
import { HttpTypes } from "@medusajs/types"
import { Button } from "@medusajs/ui"
import Divider from "@modules/common/components/divider"
import OptionSelect from "@modules/products/components/product-actions/option-select"
import { isEqual } from "lodash"
import { useParams, usePathname, useSearchParams } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"
import ProductPrice from "../product-price"
import MobileActions from "./mobile-actions"
import { useRouter } from "next/navigation"
import { useT } from "@lib/i18n/client"
import { isColorOption } from "@lib/shirt-colors"
import { useShirtColor } from "@modules/products/components/shirt-color"

type ProductActionsProps = {
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  disabled?: boolean
}

const optionsAsKeymap = (
  variantOptions: HttpTypes.StoreProductVariant["options"]
) => {
  return variantOptions?.reduce((acc: Record<string, string>, varopt: any) => {
    acc[varopt.option_id] = varopt.value
    return acc
  }, {})
}

export default function ProductActions({
  product,
  disabled,
}: ProductActionsProps) {
  const t = useT()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [options, setOptions] = useState<Record<string, string | undefined>>({})
  const [isAdding, setIsAdding] = useState(false)
  const countryCode = useParams().countryCode as string

  // Kolor koszulki mieszka w kontekście, bo steruje też makietą w galerii
  // (inna kolumna strony). Reszta opcji zostaje w lokalnym stanie.
  const { color, setColor } = useShirtColor()

  const colorOption = useMemo(
    () => (product.options || []).find((o) => isColorOption(o.title)),
    [product.options]
  )

  // Kolor nad rozmiarem — najpierw wybiera się koszulkę, potem rozmiar.
  const orderedOptions = useMemo(
    () =>
      [...(product.options || [])].sort(
        (a, b) =>
          Number(!isColorOption(a.title)) - Number(!isColorOption(b.title))
      ),
    [product.options]
  )

  // Komplet opcji do dopasowania wariantu: lokalny stan + kolor z kontekstu.
  const selectedOptions = useMemo(() => {
    if (!colorOption || !color) {
      return options
    }
    return { ...options, [colorOption.id]: color }
  }, [options, colorOption, color])

  // If there is only 1 variant, preselect the options
  useEffect(() => {
    if (product.variants?.length === 1) {
      const variantOptions = optionsAsKeymap(product.variants[0].options)
      setOptions(variantOptions ?? {})
      if (colorOption && variantOptions?.[colorOption.id]) {
        setColor(variantOptions[colorOption.id])
      }
    }
  }, [product.variants, colorOption])

  const selectedVariant = useMemo(() => {
    if (!product.variants || product.variants.length === 0) {
      return
    }

    return product.variants.find((v) => {
      const variantOptions = optionsAsKeymap(v.options)
      return isEqual(variantOptions, selectedOptions)
    })
  }, [product.variants, selectedOptions])

  // update the options when a variant is selected
  const setOptionValue = (optionId: string, value: string) => {
    if (colorOption && optionId === colorOption.id) {
      setColor(value)
      return
    }

    setOptions((prev) => ({
      ...prev,
      [optionId]: value,
    }))
  }

  //check if the selected options produce a valid variant
  const isValidVariant = useMemo(() => {
    return product.variants?.some((v) => {
      const variantOptions = optionsAsKeymap(v.options)
      return isEqual(variantOptions, selectedOptions)
    })
  }, [product.variants, selectedOptions])

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString())
    const value = isValidVariant ? selectedVariant?.id : null

    if (params.get("v_id") === value) {
      return
    }

    if (value) {
      params.set("v_id", value)
    } else {
      params.delete("v_id")
    }

    router.replace(pathname + "?" + params.toString())
  }, [selectedVariant, isValidVariant])

  // check if the selected variant is in stock
  const inStock = useMemo(() => {
    // If we don't manage inventory, we can always add to cart
    if (selectedVariant && !selectedVariant.manage_inventory) {
      return true
    }

    // If we allow back orders on the variant, we can add to cart
    if (selectedVariant?.allow_backorder) {
      return true
    }

    // If there is inventory available, we can add to cart
    if (
      selectedVariant?.manage_inventory &&
      (selectedVariant?.inventory_quantity || 0) > 0
    ) {
      return true
    }

    // Otherwise, we can't add to cart
    return false
  }, [selectedVariant])

  const actionsRef = useRef<HTMLDivElement>(null)

  const inView = useIntersection(actionsRef, "0px")

  // add the selected variant to the cart
  const handleAddToCart = async () => {
    if (!selectedVariant?.id) return null

    setIsAdding(true)

    await addToCart({
      variantId: selectedVariant.id,
      quantity: 1,
      countryCode,
    })

    setIsAdding(false)
  }

  return (
    <>
      <div className="flex flex-col gap-y-2" ref={actionsRef}>
        <div>
          {(product.variants?.length ?? 0) > 1 && (
            <div className="flex flex-col gap-y-4">
              {orderedOptions.map((option) => {
                return (
                  <div key={option.id}>
                    <OptionSelect
                      option={option}
                      current={selectedOptions[option.id]}
                      updateOption={setOptionValue}
                      title={option.title ?? ""}
                      data-testid="product-options"
                      disabled={!!disabled || isAdding}
                    />
                  </div>
                )
              })}
              <Divider />
            </div>
          )}
        </div>

        <ProductPrice product={product} variant={selectedVariant} />

        <Button
          onClick={handleAddToCart}
          disabled={
            !inStock ||
            !selectedVariant ||
            !!disabled ||
            isAdding ||
            !isValidVariant
          }
          variant="primary"
          className="w-full h-10"
          isLoading={isAdding}
          data-testid="add-product-button"
        >
          {/* Uwaga: w starterze warunkiem było `!options`, a `options` to zawsze
              obiekt (prawdziwy logicznie), więc gałąź "wybierz wariant" nigdy
              się nie wykonywała i każdy widział "Out of stock" mimo pełnego
              magazynu. Sprawdzamy brak wybranego wariantu wprost. */}
          {!selectedVariant || !isValidVariant
            ? colorOption
              ? t("Wybierz kolor i rozmiar")
              : t("Wybierz rozmiar")
            : !inStock
            ? t("Brak na stanie")
            : t("Dodaj do koszyka")}
        </Button>
        <MobileActions
          product={product}
          variant={selectedVariant}
          options={selectedOptions}
          updateOptions={setOptionValue}
          inStock={inStock}
          handleAddToCart={handleAddToCart}
          isAdding={isAdding}
          show={!inView}
          optionsDisabled={!!disabled || isAdding}
        />
      </div>
    </>
  )
}
